import axios from 'axios';

/**
 * Service for Twilio SMS integration
 * This follows the same pattern as other services in the application
 */
class TwilioService {
  constructor() {
    // Read credentials from environment variables
    this.accountSid = process.env.REACT_APP_TWILIO_ACCOUNT_SID;
    this.authToken = process.env.REACT_APP_TWILIO_AUTH_TOKEN;
    this.messagingServiceSid = process.env.REACT_APP_TWILIO_MESSAGING_SERVICE_SID;
    this.twilioPhoneNumber = process.env.REACT_APP_TWILIO_PHONE_NUMBER;
    this.apiUrl = `https://api.twilio.com/2010-04-01/Accounts/${this.accountSid}/Messages.json`;
  }

  /**
   * Send an SMS message to a specified phone number
   * 
   * @param {string} to - The recipient's phone number
   * @param {string} message - The SMS message to send
   * @returns {Promise<Object>} - The response object
   */
  async sendSMS(to, message) {
    try {
      console.log(`Sending SMS to ${to}`);
      
      // Format the phone number to E.164 format (required by Twilio)
      let formattedNumber = to;
      if (!to.startsWith('+')) {
        formattedNumber = '+1' + to.replace(/\D/g, ''); // Assuming US numbers
      }
      
      // Create form data for the request (using URLSearchParams as in the curl example)
      const formData = new URLSearchParams();
      formData.append('To', formattedNumber);
      formData.append('Body', message);
      
      // Use either Messaging Service or a specific phone number
      if (this.messagingServiceSid) {
        formData.append('MessagingServiceSid', this.messagingServiceSid);
      } else {
        formData.append('From', this.twilioPhoneNumber);
      }
      
      // Send the message using axios directly to match the curl example
      const response = await axios.post(this.apiUrl, formData, {
        auth: {
          username: this.accountSid,
          password: this.authToken
        },
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      
      console.log('Twilio API response:', response.data);
      
      return {
        success: true,
        messageId: response.data.sid,
        to: response.data.to,
        status: response.data.status
      };
    } catch (error) {
      console.error('Failed to send SMS:', error);
      throw new Error('Failed to send SMS: ' + (error.response?.data?.message || error.message));
    }
  }
  
  /**
   * Send a feedback request SMS for a specific appointment
   * 
   * @param {Object} appointment - The appointment object
   * @param {string} patientPhone - The patient's phone number
   * @param {string} patientName - The patient's name
   * @param {string} providerName - The provider's name
   * @param {string} feedbackLink - Optional custom feedback URL with provider params
   * @returns {Promise<Object>} - The SMS response
   */
  async sendFeedbackSMS(appointment, patientPhone, patientName, providerName, feedbackLink) {
    if (!patientPhone) {
      throw new Error('Patient phone number is required to send feedback SMS');
    }
    
    try {
      // Use the provided feedback link or generate the default one
      const feedbackUrl = feedbackLink || `${window.location.origin}/feedback`;
      const appointmentDate = new Date(appointment.date).toLocaleDateString();
      
      // Enhanced message to match email template format
      const message = 
        `Dear ${patientName},\n\n` +
        `Thank you for visiting Elevate Health Group for your appointment on ${appointmentDate} with ${providerName}.\n\n` +
        `We value your feedback and would like to hear about your experience. Your comments help us improve our services.\n\n` +
        `Please share your thoughts: ${feedbackUrl}\n\n` +
        `Elevate Health Group - Your Health Journey Partner`;
      
      return await this.sendSMS(patientPhone, message);
    } catch (error) {
      console.error('Failed to send feedback SMS:', error);
      throw error;
    }
  }
}

export default new TwilioService();
